import type { ClientUsageRow } from '@/state/billing/useClientUsage';
import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { DataTable, useDataControl } from '@/components/DataTable';
import { cn } from 'helpers/cn';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { formatUnits, statusLabel } from '@/state/billing/billing-format';
import { ActionButton, EmptyState, FOCUS_RING, Panel } from '../../ConsolePanel';
import { SectionStatusLine } from './SectionStatusLine';

type ClientBillingTableProps = { manager: BillingSettingsManager };

/**
 * Agency-only: every client with its own plan and its month-to-date usage (a new
 * one shows with no plan and nothing used yet). Each client has a plan of its own,
 * paid on the agency's billing account, so a client without one is read-only until
 * the agency chooses it. Choosing a client opens its plan, allowance and credits
 * above and filters the daily chart to it; "Show all" goes back to the agency's own
 * view.
 */
export const ClientBillingTable = ({ manager }: ClientBillingTableProps) => {
  const { clientUsage: section, selectedClientId, selectedClientName, showAllClients } = manager;
  const { clientUsage } = section;

  return (
    <Panel
      tourAnchor="billing-clients"
      title="Clients"
      description={
        selectedClientId
          ? `Showing ${selectedClientName ?? 'one client'}: its plan, credits and daily usage.`
          : 'Choose a client to see and change its plan, or to see its daily usage.'
      }
      actions={selectedClientId ? <ActionButton onClick={showAllClients}>Show all</ActionButton> : undefined}
    >
      {!clientUsage ? (
        <SectionStatusLine
          status={section}
          loadingText="Loading client usage..."
          errorText="Client usage could not be loaded."
        />
      ) : clientUsage.clients.length === 0 ? (
        <EmptyState>No clients yet. Clients you add appear here with their usage.</EmptyState>
      ) : (
        <ClientRows clients={clientUsage.clients} selectedClientId={selectedClientId} onSelect={manager.selectClient} />
      )}
    </Panel>
  );
};

type ClientRowsProps = {
  clients: ClientUsageRow[];
  selectedClientId: number | null;
  onSelect: (companyId: number) => void;
};

const ClientRows = ({ clients, selectedClientId, onSelect }: ClientRowsProps) => {
  const dataController = useDataControl<ClientUsageRow>(clients);

  return (
    <DataTable<ClientUsageRow>
      dataController={dataController}
      uniqueKey="companyId"
      columns={[
        {
          label: 'Client',
          field: 'companyName',
          sortKey: 'companyName',
          mobile: 'title',
          format: ({ item }) => {
            const isSelected = selectedClientId === item.companyId;
            return (
              <button
                type="button"
                aria-pressed={isSelected}
                aria-label={`${isSelected ? 'Stop showing' : 'Show'} daily usage for ${item.companyName}`}
                onClick={() => onSelect(item.companyId)}
                className={cn(
                  'rounded-sm text-left text-body underline-offset-2 hover:underline',
                  isSelected ? 'text-foreground font-medium underline' : 'text-foreground',
                  FOCUS_RING,
                )}
              >
                {item.companyName}
              </button>
            );
          },
        },
        {
          label: 'Plan',
          field: 'plan',
          sortKey: 'plan',
          format: ({ item }) =>
            item.billingMode !== 'card' ? (
              <span className="text-body">
                {item.plan ? `${BILLING_PLAN_CATALOGUE[item.plan].name}, ` : ''}
                {item.billingMode === 'invoice' ? 'billed by invoice' : 'not billed'}
              </span>
            ) : item.plan ? (
              <span className="text-body">
                {BILLING_PLAN_CATALOGUE[item.plan].name}
                {item.included ? ', included' : item.interval === 'year' ? ' (annual)' : ''}
                {item.status && item.status !== 'active' ? `, ${statusLabel(item.status).toLowerCase()}` : ''}
              </span>
            ) : (
              <span className="text-danger text-body">No plan</span>
            ),
        },
        {
          label: 'Sessions',
          field: 'units',
          sortKey: 'units',
          format: ({ item }) => <span className="num">{formatUnits(item.units)}</span>,
        },
        {
          label: 'Actions',
          mobile: 'action',
          format: ({ item }) => (
            <ActionButton onClick={() => onSelect(item.companyId)}>
              {selectedClientId === item.companyId
                ? 'Close'
                : item.billingMode !== 'card' || item.included
                  ? 'View usage'
                  : item.hasLiveSubscription
                    ? 'Manage plan'
                    : 'Choose a plan'}
            </ActionButton>
          ),
        },
      ]}
    />
  );
};
